import React, { useState } from 'react';
import { classService, BatchClassItem, BatchClassResponse } from '../services/classService';
import { PlusIcon } from './Icons';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (resp: BatchClassResponse) => void;
  schoolId: number;
  defaultYear: string;
}

type ItemMode = 'pattern' | 'names';

interface UIItem {
  mode: ItemMode;
  grade: string;
  // pattern
  namePattern: string;
  startNum: string;
  count: string;
  // names
  names: string;  // 多行文本，每行一个名字
}

function emptyItem(): UIItem {
  return {
    mode: 'pattern',
    grade: '',
    namePattern: '高一{n}班',
    startNum: '1',
    count: '8',
    names: '',
  };
}

const BatchCreateClassDialog: React.FC<Props> = ({ open, onClose, onSuccess, schoolId, defaultYear }) => {
  const [academicYear, setAcademicYear] = useState(defaultYear);
  const [items, setItems] = useState<UIItem[]>([emptyItem()]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BatchClassResponse | null>(null);
  const [error, setError] = useState('');

  if (!open) return null;

  const reset = () => {
    setItems([emptyItem()]);
    setResult(null);
    setError('');
  };
  const handleClose = () => {
    reset();
    onClose();
  };

  const updateItem = (idx: number, patch: Partial<UIItem>) => {
    setItems(prev => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));

  const totalPlanned = items.reduce((sum, it) => {
    if (it.mode === 'pattern') {
      const c = parseInt(it.count, 10);
      return sum + (isFinite(c) && c > 0 ? c : 0);
    }
    return sum + it.names.split('\n').map(s => s.trim()).filter(Boolean).length;
  }, 0);

  const buildPayloadItems = (): BatchClassItem[] => items.map(it => {
    const base: BatchClassItem = { grade: it.grade.trim() || undefined };
    if (it.mode === 'pattern') {
      base.namePattern = it.namePattern.trim();
      base.startNum = parseInt(it.startNum, 10);
      base.count = parseInt(it.count, 10);
    } else {
      base.names = it.names.split('\n').map(s => s.trim()).filter(Boolean);
    }
    return base;
  });

  const handleSubmit = async () => {
    setError('');
    if (!/^\d{4}$/.test(academicYear)) {
      setError('学年必须是 4 位数字');
      return;
    }
    if (totalPlanned === 0) {
      setError('请至少配置一个班级');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await classService.batchCreate({
        schoolId,
        academicYear,
        items: buildPayloadItems(),
      });
      setResult(resp);
      onSuccess(resp);
    } catch (e: any) {
      setError(e?.response?.data?.message || '批量创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-surface-container-lowest rounded-3xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between">
          <div>
            <h2 className="text-xl font-headline text-on-surface">批量创建班级</h2>
            <p className="text-xs text-on-surface-variant mt-0.5">
              支持模式批量（如 高一{`{n}`}班）和班名列表两种方式
            </p>
          </div>
          <button onClick={handleClose} className="text-on-surface-variant hover:text-on-surface text-2xl leading-none">
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {!result && (
            <>
              <div>
                <label className="block text-sm font-label text-on-surface mb-1.5">学年</label>
                <input
                  value={academicYear}
                  onChange={e => setAcademicYear(e.target.value)}
                  placeholder="如 2024"
                  className="w-32 h-10 px-3 bg-surface-container rounded-xl text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div className="space-y-4">
                {items.map((it, idx) => (
                  <div key={idx} className="border border-outline-variant rounded-2xl p-4 bg-surface-container">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm text-on-surface font-medium">第 {idx + 1} 组</span>
                        <select
                          value={it.mode}
                          onChange={e => updateItem(idx, { mode: e.target.value as ItemMode })}
                          className="h-9 px-3 bg-surface-container-high rounded-lg text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                        >
                          <option value="pattern">模板批量</option>
                          <option value="names">班名列表</option>
                        </select>
                      </div>
                      {items.length > 1 && (
                        <button onClick={() => removeItem(idx)}
                                className="text-sm text-danger-600 hover:text-danger-800">
                          删除
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-on-surface-variant mb-1">年级（可选）</label>
                        <input
                          value={it.grade}
                          onChange={e => updateItem(idx, { grade: e.target.value })}
                          placeholder="高一 / 高二 ..."
                          className="w-full h-10 px-3 bg-surface-container-high rounded-xl text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                        />
                      </div>

                      {it.mode === 'pattern' ? (
                        <>
                          <div>
                            <label className="block text-xs text-on-surface-variant mb-1">
                              班名模板（用 <code>{`{n}`}</code> 占位）
                            </label>
                            <input
                              value={it.namePattern}
                              onChange={e => updateItem(idx, { namePattern: e.target.value })}
                              placeholder="例如 高一{n}班"
                              className="w-full h-10 px-3 bg-surface-container-high rounded-xl text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-on-surface-variant mb-1">起始序号</label>
                            <input
                              type="number"
                              min={1}
                              value={it.startNum}
                              onChange={e => updateItem(idx, { startNum: e.target.value })}
                              className="w-full h-10 px-3 bg-surface-container-high rounded-xl text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-on-surface-variant mb-1">个数（≤200）</label>
                            <input
                              type="number"
                              min={1}
                              max={200}
                              value={it.count}
                              onChange={e => updateItem(idx, { count: e.target.value })}
                              className="w-full h-10 px-3 bg-surface-container-high rounded-xl text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                          </div>
                          <div className="md:col-span-2 text-xs text-on-surface-variant bg-surface-container-low rounded-lg px-3 py-2">
                            预览：{(() => {
                              const start = parseInt(it.startNum, 10);
                              const count = parseInt(it.count, 10);
                              if (!isFinite(start) || !isFinite(count) || count < 1) return '—';
                              const sample = Math.min(count, 3);
                              const names = [];
                              for (let i = 0; i < sample; i++) {
                                names.push(it.namePattern.replace('{n}', String(start + i)));
                              }
                              return count > sample
                                ? `${names.join('、')} ... 共 ${count} 个`
                                : names.join('、');
                            })()}
                          </div>
                        </>
                      ) : (
                        <div className="md:col-span-2">
                          <label className="block text-xs text-on-surface-variant mb-1">
                            班名列表（每行一个）
                          </label>
                          <textarea
                            value={it.names}
                            onChange={e => updateItem(idx, { names: e.target.value })}
                            rows={4}
                            placeholder="国际班&#10;实验班&#10;强基班"
                            className="w-full px-3 py-2 bg-surface-container-high rounded-xl text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 font-body"
                          />
                          <p className="text-xs text-on-surface-variant mt-1">
                            将创建 {it.names.split('\n').map(s => s.trim()).filter(Boolean).length} 个班
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <button onClick={addItem}
                        className="w-full h-11 border-2 border-dashed border-outline-variant text-on-surface-variant rounded-xl hover:border-primary-300 hover:text-primary-700 transition flex items-center justify-center space-x-2">
                  <PlusIcon size={18} />
                  <span>添加一组</span>
                </button>
              </div>

              {error && (
                <div className="bg-danger-50 border border-danger-200 text-danger-800 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}
            </>
          )}

          {result && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-success-50 border border-success-200 rounded-xl p-4">
                  <div className="text-3xl font-bold text-success-700">{result.created}</div>
                  <div className="text-xs text-success-800 mt-1">已创建</div>
                </div>
                <div className="bg-warning-50 border border-warning-200 rounded-xl p-4">
                  <div className="text-3xl font-bold text-warning-700">{result.skipped}</div>
                  <div className="text-xs text-warning-800 mt-1">跳过（重名）</div>
                </div>
                <div className="bg-danger-50 border border-danger-200 rounded-xl p-4">
                  <div className="text-3xl font-bold text-danger-700">{result.failed}</div>
                  <div className="text-xs text-danger-800 mt-1">失败</div>
                </div>
              </div>
              <div className="border border-outline-variant rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-surface-container-low text-on-surface-variant">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">班名</th>
                      <th className="text-left px-3 py-2 font-medium">classId</th>
                      <th className="text-left px-3 py-2 font-medium">状态</th>
                      <th className="text-left px-3 py-2 font-medium">说明</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.items.map((it, i) => (
                      <tr key={i} className="border-t border-outline-variant">
                        <td className="px-3 py-2 text-on-surface">{it.name}</td>
                        <td className="px-3 py-2 text-on-surface-variant font-mono text-xs">{it.classId || '—'}</td>
                        <td className="px-3 py-2">
                          <span className={
                            it.status === 'CREATED' ? 'text-success-700' :
                            it.status === 'SKIPPED' ? 'text-warning-700' : 'text-danger-700'
                          }>
                            {it.status === 'CREATED' ? '已创建' : it.status === 'SKIPPED' ? '跳过' : '失败'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-on-surface-variant">{it.message || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-outline-variant flex items-center justify-between">
          <span className="text-sm text-on-surface-variant">
            {result ? '完成' : `计划创建 ${totalPlanned} 个班级`}
          </span>
          <div className="space-x-2">
            {!result ? (
              <>
                <button onClick={handleClose}
                        className="h-10 px-5 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low transition">
                  取消
                </button>
                <button onClick={handleSubmit}
                        disabled={submitting || totalPlanned === 0}
                        className="h-10 px-5 gradient-primary text-on-primary rounded-xl shadow-teal disabled:opacity-50 disabled:cursor-not-allowed transition font-medium">
                  {submitting ? '创建中…' : '开始创建'}
                </button>
              </>
            ) : (
              <button onClick={handleClose}
                      className="h-10 px-5 gradient-primary text-on-primary rounded-xl shadow-teal transition font-medium">
                关闭
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchCreateClassDialog;
